import { useEffect, useRef } from 'react';

/**
 * Silently re-runs `poll` on an interval for as long as its result still
 * contains a pending row — used to keep resource dashboards (decks, and
 * eventually quizzes/summaries) fresh while generation runs in the
 * background, without a visible loading state. Mirrors AIResponseLoader's
 * await-then-reschedule pattern (decides whether to continue from the poll's
 * own fresh result, not from a re-render) so requests can't overlap.
 * @param {Object} params
 * @param {() => Promise<Array>} params.poll Refetches and returns the current rows
 * @param {(row: Object) => boolean} params.isPending Whether a row should keep polling going
 * @param {boolean} params.enabled Whether polling should start (e.g. the current rows already contain a pending one)
 * @param {number} [params.intervalMs]
 */
function usePollWhilePending({ poll, isPending, enabled, intervalMs = 15 * 1000 }) {
  // useRef(poll) only seeds .current on the first render — React ignores the
  // argument after that, so these explicit assignments are what keep the
  // refs pointing at the latest poll/isPending on every render (e.g. poll
  // closing over a fresh courseId). Refs, not deps, so the effect below
  // doesn't tear down and restart its timer every time a new function
  // reference comes in — it only reacts to enabled/intervalMs changing.
  const pollRef = useRef(poll);
  const isPendingRef = useRef(isPending);
  pollRef.current = poll;
  isPendingRef.current = isPending;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let timeoutId;

    const tick = async () => {
      try {
        const rows = await pollRef.current();
        if (cancelled) return;
        if (rows.some(isPendingRef.current)) {
          timeoutId = setTimeout(tick, intervalMs);
        }
      } catch (err) {
        console.log(err);
        if (!cancelled) {
          timeoutId = setTimeout(tick, intervalMs);
        }
      }
    };

    timeoutId = setTimeout(tick, intervalMs);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [enabled, intervalMs]);
}

export default usePollWhilePending;
